import React, { useState, useEffect } from 'react';
import {
  PlusIcon,
  MinusIcon,
  Bars3Icon,
  EyeIcon,
  LockClosedIcon,
  LockOpenIcon,
  PencilSquareIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { api } from '../../services/authService';

const ClauseSelector = ({ selectedClauses = [], onChange }) => {
  const [allClauses, setAllClauses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [previewClause, setPreviewClause] = useState(null);
  const [draggedItem, setDraggedItem] = useState(null);

  useEffect(() => {
    loadClauses();
    loadCategories();
  }, []);

  const loadClauses = async () => {
    try {
      const response = await api.get('/api/contracts/clauses/list');
      const data = response.data;
      setAllClauses(data.clauses || []);
    } catch (error) {
      console.error('Erro ao carregar cláusulas:', error);
      toast.error('Erro ao carregar cláusulas');
      if (error.response?.status === 401) {
        toast.error('Sessão expirada. Faça login novamente.');
      }
    }
  };

  const loadCategories = async () => {
    try {
      const response = await api.get('/api/contracts/clauses/categories');
      const data = response.data;
      setCategories(data.categories || []);
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
      if (error.response?.status === 401) {
        toast.error('Sessão expirada. Faça login novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  const isClauseSelected = (clauseId) => {
    return selectedClauses.some((c) => c.id === clauseId);
  };

  const toggleClause = (clause) => {
    if (clause.is_optional === 0) {
      toast.error('Esta cláusula é obrigatória e não pode ser removida');
      return;
    }

    let newSelected;
    if (isClauseSelected(clause.id)) {
      newSelected = selectedClauses.filter((c) => c.id !== clause.id);
      toast.success(`Cláusula "${clause.title}" removida`);
    } else {
      newSelected = [...selectedClauses, clause];
      toast.success(`Cláusula "${clause.title}" adicionada`);
    }

    if (onChange) {
      onChange(newSelected);
    }
  };

  const handleDragStart = (e, clause) => {
    console.log('🎯 Drag START:', clause.title);
    setDraggedItem(clause);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, targetClause) => {
    e.preventDefault();
    console.log('🎯 Drop sobre:', targetClause.title);

    if (!draggedItem) {
      console.log('❌ Nenhum item sendo arrastado');
      return;
    }

    const draggedIndex = selectedClauses.findIndex((c) => c.id === draggedItem.id);
    const targetIndex = selectedClauses.findIndex((c) => c.id === targetClause.id);

    if (draggedIndex === -1 || targetIndex === -1) return;

    const newSelected = [...selectedClauses];
    const [removed] = newSelected.splice(draggedIndex, 1);
    newSelected.splice(targetIndex, 0, removed);

    if (onChange) {
      onChange(newSelected);
    }

    // Salvar nova ordem no banco de dados
    await saveClauseOrder(newSelected);

    setDraggedItem(null);
  };

  const saveClauseOrder = async (clauses) => {
    try {
      // Criar array de IDs na nova ordem
      const clauseIds = clauses.map(c => c.id);

      await api.put('/api/contracts/clauses/reorder', {
        clause_ids: clauseIds
      });

      toast.success('Ordem das cláusulas atualizada!');
    } catch (error) {
      console.error('Erro ao salvar ordem:', error);
      toast.error('Erro ao salvar ordem das cláusulas');
    }
  };

  const clausesByCategory = categories.map((category) => {
    const clauses = allClauses.filter((clause) => clause.category_id === category.id);
    const editableClauses = clauses.filter((c) => c.is_optional === 1);
    return {
      ...category,
      clauses,
      editableCount: editableClauses.length,
      hasEditableClauses: editableClauses.length > 0
    };
  });

  const selectedClausesWithOrder = selectedClauses.sort((a, b) => a.display_order - b.display_order);

  if (loading) {
    return <div className="text-center py-8">Carregando cláusulas...</div>;
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Biblioteca de Cláusulas */}
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6">
        <h3 className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">
          Biblioteca de Cláusulas
        </h3>

        {/* Dica visual */}
        <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
          <div className="flex items-start gap-2">
            <PencilSquareIcon className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="text-green-800 dark:text-green-200 font-medium">
                Categorias com fundo verde têm cláusulas editáveis
              </p>
              <p className="text-green-700 dark:text-green-300 text-xs mt-1">
                Clique para expandir e adicionar/remover as cláusulas opcionais
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-3">
          {clausesByCategory.map((category) => (
            <div
              key={category.id}
              className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
            >
              {/* Cabeçalho da Categoria */}
              <button
                onClick={() =>
                  setExpandedCategory(
                    expandedCategory === category.id ? null : category.id
                  )
                }
                className={`w-full px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center justify-between transition-colors ${
                  category.hasEditableClauses
                    ? 'bg-green-50 dark:bg-green-900/20'
                    : 'bg-gray-50 dark:bg-gray-900'
                }`}
              >
                <div className="flex items-center gap-2">
                  <span className="text-lg">{category.icon || '📄'}</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {category.name}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    ({category.clauses.length})
                  </span>
                  {category.hasEditableClauses && (
                    <div className="flex items-center gap-1 ml-2">
                      <PencilSquareIcon className="w-4 h-4 text-green-600 dark:text-green-400" />
                      <span className="px-2 py-0.5 bg-green-100 dark:bg-green-800 text-green-700 dark:text-green-300 text-xs font-medium rounded">
                        {category.editableCount} editável{category.editableCount > 1 ? 'is' : ''}
                      </span>
                    </div>
                  )}
                </div>
                <svg
                  className={`w-5 h-5 text-gray-500 transition-transform ${
                    expandedCategory === category.id ? 'rotate-180' : ''
                  }`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>

              {/* Cláusulas da Categoria */}
              {expandedCategory === category.id && (
                <div className="p-3 space-y-2">
                  {category.clauses.map((clause) => {
                    const isSelected = isClauseSelected(clause.id);
                    const isRequired = clause.is_optional === 0;

                    return (
                      <div
                        key={clause.id}
                        className={`
                          p-3 rounded-lg border transition-all
                          ${
                            isSelected
                              ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
                              : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                          }
                        `}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                                {clause.title}
                              </h4>
                              {isRequired && (
                                <LockClosedIcon className="w-4 h-4 text-gray-400" title="Obrigatória" />
                              )}
                            </div>
                            <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2">
                              {clause.content.substring(0, 100)}...
                            </p>
                          </div>

                          <div className="flex gap-1 flex-shrink-0">
                            <button
                              onClick={() => setPreviewClause(clause)}
                              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                              title="Visualizar"
                            >
                              <EyeIcon className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                            </button>

                            {!isRequired && (
                              <button
                                onClick={() => toggleClause(clause)}
                                className={`
                                  p-2 rounded transition-colors
                                  ${
                                    isSelected
                                      ? 'bg-red-100 dark:bg-red-900/20 hover:bg-red-200 dark:hover:bg-red-900/40 text-red-600 dark:text-red-400'
                                      : 'bg-green-100 dark:bg-green-900/20 hover:bg-green-200 dark:hover:bg-green-900/40 text-green-600 dark:text-green-400'
                                  }
                                `}
                                title={isSelected ? 'Remover' : 'Adicionar'}
                              >
                                {isSelected ? (
                                  <MinusIcon className="w-4 h-4" />
                                ) : (
                                  <PlusIcon className="w-4 h-4" />
                                )}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Cláusulas Selecionadas */}
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Cláusulas Selecionadas
          </h3>
          <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded-full text-sm font-medium">
            {selectedClausesWithOrder.length}
          </span>
        </div>

        {selectedClausesWithOrder.length === 0 ? (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            <p>Nenhuma cláusula selecionada</p>
            <p className="text-sm mt-2">
              Adicione cláusulas da biblioteca ao lado
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {selectedClausesWithOrder.map((clause, index) => {
              const isRequired = clause.is_optional === 0;

              return (
                <div
                  key={clause.id}
                  draggable={true}
                  onDragStart={(e) => handleDragStart(e, clause)}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, clause)}
                  className={`
                    p-3 rounded-lg border-2 transition-all
                    ${draggedItem?.id === clause.id
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 opacity-50'
                      : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900'
                    }
                    cursor-move hover:shadow-md hover:border-blue-300
                  `}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 pt-1">
                      <Bars3Icon className="w-5 h-5 text-gray-400" />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                          {index + 1}.
                        </span>
                        <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                          {clause.title}
                        </h4>
                        {isRequired && (
                          <span className="px-2 py-0.5 bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 text-xs rounded">
                            Obrigatória
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-1">
                        {clause.content.substring(0, 80)}...
                      </p>
                    </div>

                    <div className="flex gap-1 flex-shrink-0">
                      <button
                        onClick={() => setPreviewClause(clause)}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                      >
                        <EyeIcon className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                      </button>

                      {!isRequired && (
                        <button
                          onClick={() => toggleClause(clause)}
                          className="p-2 hover:bg-red-100 dark:hover:bg-red-900/20 rounded transition-colors"
                        >
                          <MinusIcon className="w-4 h-4 text-red-600 dark:text-red-400" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {selectedClausesWithOrder.length > 0 && (
          <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-sm text-blue-700 dark:text-blue-300">
            💡 Arraste e solte as cláusulas para reordenar (ordem será salva automaticamente)
          </div>
        )}
      </div>

      {/* Modal de Preview */}
      {previewClause && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setPreviewClause(null)}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1">
                    {previewClause.title}
                  </h3>
                  {previewClause.is_optional === 0 && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 text-xs rounded">
                      <LockClosedIcon className="w-3 h-3" />
                      Cláusula Obrigatória
                    </span>
                  )}
                </div>
                <button
                  onClick={() => setPreviewClause(null)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                >
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="prose dark:prose-invert max-w-none">
                <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                  {previewClause.content}
                </p>
              </div>

              <div className="mt-6 flex justify-end gap-2">
                <button
                  onClick={() => setPreviewClause(null)}
                  className="px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg transition-colors"
                >
                  Fechar
                </button>
                {previewClause.is_optional === 1 && (
                  <button
                    onClick={() => {
                      toggleClause(previewClause);
                      setPreviewClause(null);
                    }}
                    className={`
                      px-4 py-2 rounded-lg transition-colors
                      ${
                        isClauseSelected(previewClause.id)
                          ? 'bg-red-600 hover:bg-red-700 text-white'
                          : 'bg-blue-600 hover:bg-blue-700 text-white'
                      }
                    `}
                  >
                    {isClauseSelected(previewClause.id) ? 'Remover' : 'Adicionar'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClauseSelector;